import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { mutations } from 'RestAPIs/rapini';
import { showSnackbar } from 'components/shared';

import type {
  EditAppointmentTypeResponse,
  EventResponse,
  ExportAppointmentOccurrenceSelectionRequest
} from 'generated/index';

type UseBulkReExportAppointmentsParams = {
  onSuccess: () => void;
};

/**
 * Re-exports the selected appointments to the external systems (ECARIS, Reflex).
 *
 * The export re-derives everything it derives at export time, the care receiver's treatment above
 * all, so re-exporting is how a treatment that changed after the original export reaches the
 * external system. The backend answers as soon as the run has started, not when it has finished.
 */
export const useBulkReExportAppointments = ({
  onSuccess
}: UseBulkReExportAppointmentsParams) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const { mutate, isLoading } =
    mutations.useExportCalculatedAppointmentOccurrenceSelection({
      onSuccess: (data) => {
        queryClient.invalidateQueries('appointmentsOverview');
        onSuccess();

        showSnackbar({
          severity: 'success',
          message: t('appointmentsOverview.export.success', {
            count: data?.seriesDispatched ?? 0
          })
        });
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('appointmentsOverview.export.failure')
        });
      }
    });

  const reExportAppointments = (
    appointments: EventResponse[],
    editAppointmentType: EditAppointmentTypeResponse
  ) => {
    const payload: ExportAppointmentOccurrenceSelectionRequest = {
      occurrences: appointments
        // An occurrence without its series cannot be exported: the export selects on the series.
        .filter((appointment) => !!appointment.originalAppointmentId)
        .map((appointment) => ({
          originalAppointmentId: appointment.originalAppointmentId as string,
          occurrenceDate: appointment.date as string
        })),
      editAppointmentType
    };

    if (payload.occurrences.length === 0) {
      showSnackbar({
        severity: 'error',
        message: t('appointmentsOverview.export.failure')
      });
      return;
    }

    mutate(payload);
  };

  return { reExportAppointments, isLoading };
};
